import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { ReasonOption } from '@/constants/cancellationReasons'

export interface CancellationReasonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  reasons: ReasonOption[]
  reasonRequired?: boolean
  showAmount?: boolean
  amountLabel?: string
  showDescription?: boolean
  descriptionLabel?: string
  confirmLabel: string
  confirmVariant?: 'destructive' | 'default'
  isLoading?: boolean
  onConfirm: (data: {
    reason: string
    customReason?: string
    amount?: string
    description?: string
  }) => void
}

export function CancellationReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  reasons,
  reasonRequired = true,
  showAmount = false,
  amountLabel,
  showDescription = false,
  descriptionLabel,
  confirmLabel,
  confirmVariant = 'destructive',
  isLoading = false,
  onConfirm,
}: CancellationReasonDialogProps) {
  const { t, locale } = useLocalization()

  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [amount, setAmount] = useState('')
  const [additionalDescription, setAdditionalDescription] = useState('')

  useEffect(() => {
    if (!open) {
      setSelectedReason('')
      setCustomReason('')
      setAmount('')
      setAdditionalDescription('')
    }
  }, [open])

  const isOther = selectedReason === 'other'
  const hasValidReason = reasonRequired
    ? selectedReason && (!isOther || customReason.trim())
    : true
  const hasValidAmount = !showAmount || !amount || parseFloat(amount) > 0
  const canSubmit = hasValidReason && hasValidAmount && !isLoading

  const handleConfirm = () => {
    if (!canSubmit) return
    const finalReason = isOther ? customReason.trim() : selectedReason
    onConfirm({
      reason: finalReason,
      customReason: isOther ? customReason.trim() : undefined,
      amount: showAmount && amount ? amount : undefined,
      description: showDescription && additionalDescription.trim() ? additionalDescription.trim() : undefined,
    })
  }

  const getLabel = (opt: ReasonOption) =>
    locale === 'ar' ? opt.labelAr : opt.labelEn

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Reason Select */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {reasonRequired
                ? t('cancellation.reasonRequired')
                : t('cancellation.reasonOptional')}
            </label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger>
                <SelectValue placeholder={t('cancellation.selectReason')} />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {getLabel(opt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom reason text if "Other" is selected */}
          {isOther && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('cancellation.specifyReason')}
              </label>
              <Textarea
                placeholder={t('cancellation.enterReason')}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                className="resize-none"
                rows={3}
                maxLength={500}
              />
            </div>
          )}

          {/* Amount field (for refunds) */}
          {showAmount && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                {amountLabel || t('cancellation.refundAmount')}
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('cancellation.amountHint')}
              </p>
            </div>
          )}

          {/* Additional description (for finance refunds) */}
          {showDescription && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                {descriptionLabel || t('cancellation.additionalDetails')}
              </label>
              <Textarea
                placeholder={t('cancellation.enterDetails')}
                value={additionalDescription}
                onChange={(e) => setAdditionalDescription(e.target.value)}
                className="resize-none"
                rows={2}
                maxLength={2000}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin me-2" />
            ) : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
